/**
 * The brush: shape, falloff, and stroke sampling. Shared by the paint tool and the sculpt tool, and
 * entirely pure — no Three.js, no DOM — because this is the part that has to be *right*.
 *
 * ## Why a stroke is not a list of pointer events
 *
 * The naive brush applies one dab per `pointermove`. It is wrong in two directions at once. Move the
 * mouse fast and the events arrive far apart, leaving a dotted line with gaps. Move it slowly, or run
 * at 144 Hz instead of 60, and dozens of events land on the same square centimetre, so the same
 * gesture bites far harder. The result is a tool whose output depends on the user's mouse speed and
 * the machine's frame rate — the two things a drawing should never depend on.
 *
 * So a stroke here is a *path*, resampled at a fixed spacing in world metres. Between two pointer
 * samples the tool walks the segment emitting dabs every `spacing × radius` metres, carrying the
 * leftover distance into the next segment so the rhythm is unbroken across events. Drag at any speed
 * and you get the same density of dabs per metre of ground.
 *
 * Standing still is handled separately and deliberately ({@link holdDabCount}): a stationary pointer
 * emits dabs on a wall clock, at a fixed rate, so holding the brush over one spot keeps working the
 * way it does in every paint and sculpt application. The two mechanisms never both fire for the same
 * motion — the tool only asks for hold dabs on a tick where the pointer did not move.
 */

/** How hard a stroke is applied and how it is shaped. Radii and spacing are in metres. */
export interface BrushShape {
  /** Outer radius in world metres. The visible ring. */
  radiusMeters: number;
  /**
   * Where the falloff begins, as a fraction of the radius. 0 feathers from the very centre, 0.9 is
   * an almost hard edge. Never exactly 1, so an edge always has *some* gradient to antialias it.
   */
  hardness: number;
  /** Contribution of a single dab, in [0, 1]. Paint calls it flow; sculpt scales metres by it. */
  flow: number;
  /**
   * Ceiling on what one continuous stroke may accumulate at any one point, in [0, 1]. Paint uses it;
   * sculpt leaves it at 1 because there is no meaningful "fully dug".
   */
  opacity: number;
  /** Dab interval as a fraction of the radius. 0.25 is the usual compromise: smooth, not wasteful. */
  spacing: number;
}

export const DEFAULT_BRUSH: BrushShape = {
  radiusMeters: 1.5,
  hardness: 0.5,
  flow: 0.35,
  opacity: 1,
  spacing: 0.25,
};

/** Brush radius is clamped to something a person can actually aim, at both ends. */
export const MIN_BRUSH_RADIUS = 0.1;
export const MAX_BRUSH_RADIUS = 25;

export function clampBrushRadius(meters: number): number {
  if (!Number.isFinite(meters)) return DEFAULT_BRUSH.radiusMeters;
  return Math.min(MAX_BRUSH_RADIUS, Math.max(MIN_BRUSH_RADIUS, meters));
}

export function clampUnit(value: number, fallback = 0): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(1, Math.max(0, value));
}

/**
 * Brush weight at a distance from the dab centre, in [0, 1].
 *
 * Flat 1 out to `hardness × radius`, then a smoothstep to 0 at the rim. Smoothstep rather than a
 * linear ramp because its zero derivative at both ends is what stops a wide soft brush from leaving
 * a visible seam where two overlapping strokes meet.
 *
 * Distance is metres, not pixels: a 2 m brush covers 2 m of ground whether the camera is overhead or
 * down at the lawn. Screen-space brushes feel fine until you zoom, at which point every stroke you
 * have already made is the wrong size.
 */
export function brushFalloff(distanceMeters: number, radiusMeters: number, hardness: number): number {
  if (radiusMeters <= 0) return 0;
  const t = distanceMeters / radiusMeters;
  if (t >= 1) return 0;
  if (t <= 0) return 1;
  const core = Math.min(0.98, Math.max(0, hardness));
  if (t <= core) return 1;
  const k = (t - core) / (1 - core);
  // 1 - smoothstep(k): 1 at the core edge, 0 at the rim, flat at both.
  return 1 - k * k * (3 - 2 * k);
}

/** A point on the ground, in terrain-local metres. */
export interface BrushPoint {
  x: number;
  z: number;
}

/** Dabs produced by one segment of a stroke, plus the distance carried into the next. */
export interface StrokeSampling {
  dabs: BrushPoint[];
  /** Distance travelled since the last emitted dab. Feed this back on the next call. */
  carryMeters: number;
}

/**
 * Resample the segment `from → to` into evenly spaced dab centres.
 *
 * `carryMeters` is how far the pointer had already travelled past the previous dab when the last
 * segment ended; passing it back in is what makes a stroke's spacing continuous across pointer
 * events rather than restarting at every one. A segment shorter than the remaining interval emits
 * nothing and simply grows the carry.
 */
export function sampleStrokeSegment(
  from: BrushPoint,
  to: BrushPoint,
  spacingMeters: number,
  carryMeters = 0,
  maxDabs = 4096,
): StrokeSampling {
  const spacing = Math.max(1e-4, spacingMeters);
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const length = Math.hypot(dx, dz);
  if (length <= 0) return { dabs: [], carryMeters };

  const dabs: BrushPoint[] = [];
  let travelled = 0;
  let sinceLast = Math.max(0, carryMeters);

  while (dabs.length < maxDabs) {
    const needed = spacing - sinceLast;
    if (travelled + needed > length) break;
    travelled += needed;
    sinceLast = 0;
    const t = travelled / length;
    dabs.push({ x: from.x + dx * t, z: from.z + dz * t });
  }

  return { dabs, carryMeters: sinceLast + (length - travelled) };
}

/** Dabs per second emitted while the pointer is held still. Matches the feel of a held airbrush. */
export const HOLD_DABS_PER_SECOND = 30;

/**
 * How many dabs a stationary held brush owes for `heldSeconds`, and how much time is left over.
 *
 * Time-based rather than frame-based, so a 30 fps machine and a 144 fps machine dig the same hole in
 * the same second. Capped per call so a stalled tab does not resume by applying four hundred dabs at
 * once.
 */
export function holdDabCount(
  heldSeconds: number,
  dabsPerSecond = HOLD_DABS_PER_SECOND,
  maxDabs = 8,
): { count: number; remainderSeconds: number } {
  const interval = 1 / Math.max(1, dabsPerSecond);
  if (!Number.isFinite(heldSeconds) || heldSeconds < interval) {
    return { count: 0, remainderSeconds: Math.max(0, heldSeconds) };
  }
  const wanted = Math.floor(heldSeconds / interval);
  const count = Math.min(maxDabs, wanted);
  return { count, remainderSeconds: heldSeconds - wanted * interval };
}

/**
 * The texel/vertex-space rectangle a dab of `radius` around a point covers on a grid of `cols × rows`
 * spanning `widthMeters × lengthMeters`, centred on the origin. Returned half-open and unclipped;
 * callers clip against their own grid.
 */
export function dabGridBounds(
  center: BrushPoint,
  radiusMeters: number,
  cols: number,
  rows: number,
  widthMeters: number,
  lengthMeters: number,
): { x: number; y: number; width: number; height: number } {
  const toCol = (x: number): number => (x / widthMeters + 0.5) * cols;
  const toRow = (z: number): number => (z / lengthMeters + 0.5) * rows;
  const x0 = Math.floor(toCol(center.x - radiusMeters));
  const x1 = Math.ceil(toCol(center.x + radiusMeters));
  const y0 = Math.floor(toRow(center.z - radiusMeters));
  const y1 = Math.ceil(toRow(center.z + radiusMeters));
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
}
